import { useEffect, useRef } from 'react';
import { Modal, Typography, Tag } from '@douyinfe/semi-ui';
import { IconGift } from '@douyinfe/semi-icons';
import { getCache, setCache } from '@/hook/useCache';
import { CacheEnum } from '@/enum/cache';
import useToolboxStore from '@/components/Toolbox/store';

interface ReleaseNote {
  version: string;
  date: string;
  changes: string[];
}

interface ExtensionMeta {
  version: string;
  latestZip: string;
  builtAt?: string;
  releaseNotes?: ReleaseNote[];
}

const showUpdateModal = (
  version: string,
  notes: ReleaseNote,
  onClose: () => void
) => {
  const { Text, Paragraph } = Typography;

  Modal.info({
    title: (
      <div className='flex items-center gap-2'>
        <IconGift className='text-blue-500' size='large' />
        <span>插件已更新到 v{version}</span>
      </div>
    ),
    content: (
      <div className='py-2'>
        <div className='flex items-center gap-2 mb-4'>
          <Tag color='blue' size='large'>
            v{notes.version}
          </Tag>
          <Text type='tertiary'>{notes.date}</Text>
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <Paragraph strong className='mb-3 text-gray-700'>
            更新内容：
          </Paragraph>
          <ul className='space-y-2 inline-block text-left'>
            {notes.changes.map((change, index) => (
              <li key={index} className='flex items-start gap-2'>
                <span className='text-blue-500 mt-1'>•</span>
                <Text>{change}</Text>
              </li>
            ))}
          </ul>
        </div>
      </div>
    ),
    footer: (
      <div className='flex justify-center pt-2 pb-1'>
        <button
          className='bg-blue-500 hover:bg-blue-600 text-white px-8 py-2 rounded-md text-base font-medium transition-colors'
          onClick={() => {
            setCache({
              key: CacheEnum.extensionLastReadVersion,
              value: version,
              storage: localStorage,
            });
            Modal.destroyAll();
            onClose();
          }}
        >
          关闭
        </button>
      </div>
    ),
    width: 480,
    centered: true,
    closable: false,
    maskClosable: false,
  });
};

const VersionUpdateNotification: React.FC = () => {
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // 防止 StrictMode 下重复执行
    if (hasCheckedRef.current) {
      return;
    }
    hasCheckedRef.current = true;

    const checkVersion = async () => {
      try {
        const publicUrl = process.env.PUBLIC_URL || '';
        const metaUrl = `${publicUrl}/extensions/noon-extension-chrome-mv3-prod.meta.json`;

        const resp = await fetch(`${metaUrl}?t=${Date.now()}`, {
          cache: 'no-store',
        });

        if (!resp.ok) {
          return;
        }

        const meta: ExtensionMeta = await resp.json();
        const { version, releaseNotes: notes } = meta;

        if (!version || !notes || notes.length === 0) {
          return;
        }

        // 获取已读版本
        const lastReadVersion = getCache({
          key: CacheEnum.extensionLastReadVersion,
          storage: localStorage,
        });

        // 判断是否需要显示
        if (lastReadVersion !== version) {
          const currentNotes = notes.find((n) => n.version === version);
          if (currentNotes) {
            // 抑制工具箱引导，避免同时显示多个提示
            const { setGuideSuppressed } = useToolboxStore.getState();
            setGuideSuppressed(true);
            showUpdateModal(version, currentNotes, () => {
              // Modal 关闭后恢复工具箱引导
              setGuideSuppressed(false);
            });
          }
        }
      } catch (error) {
        console.error(
          '[VersionUpdate] Failed to check extension version:',
          error
        );
      }
    };

    checkVersion();
  }, []);

  // 这个组件不渲染任何 UI，只负责触发检查逻辑
  return null;
};

export default VersionUpdateNotification;
